import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * Adds every line of the "frequently bought together" card to the cart in one
 * request.
 *
 * The card ships a plain multi-line `<form action="/cart/add">` so it still works
 * without JS, but submitting that natively navigates the shopper off the product
 * page to /cart. Shopify's standard actions do not intercept it either, so the
 * cart drawer never opens and the header bubble never updates. This element takes
 * the submit over and posts the same lines to `/cart/add.js`, then emits the
 * standard cart event the rest of the theme already listens to - the same one
 * `product-form-component` emits for a single add - so the bubble, the drawer and
 * the cart section all refresh in place.
 */
class PdpBundleForm extends HTMLElement {
  /** How long the button holds its "added" label before reverting. */
  static ADDED_LABEL_DURATION = 2500;

  /** @type {number | undefined} */
  #labelTimeout;

  connectedCallback() {
    this.#form?.addEventListener('submit', this.#onSubmit);
  }

  disconnectedCallback() {
    this.#form?.removeEventListener('submit', this.#onSubmit);
    clearTimeout(this.#labelTimeout);
  }

  /** @returns {HTMLFormElement | null} */
  get #form() {
    return this.querySelector('form');
  }

  /** @returns {HTMLButtonElement | null} */
  get #button() {
    return this.querySelector('[data-pdp-bundle-submit]');
  }

  /** @returns {HTMLElement | null} */
  get #error() {
    return this.querySelector('[data-pdp-bundle-error]');
  }

  /**
   * The lines the card rendered, read back off the hidden inputs so the JS and
   * no-JS paths always post the same thing.
   * @returns {Array<{id: number, quantity: number}>}
   */
  get #lines() {
    /** @type {Map<string, Record<string, number>>} */
    const lines = new Map();

    for (const input of this.querySelectorAll('input[name^="items["]')) {
      const match = /^items\[(\d+)\]\[(id|quantity)\]$/.exec(/** @type {HTMLInputElement} */ (input).name);
      if (!match) continue;

      const [, index, key] = match;
      const line = lines.get(index) ?? {};
      line[key] = Number(/** @type {HTMLInputElement} */ (input).value);
      lines.set(index, line);
    }

    return [...lines.values()]
      .filter((line) => Number.isFinite(line.id) && Number.isFinite(line.quantity) && line.quantity > 0)
      .map((line) => ({ id: line.id, quantity: line.quantity }));
  }

  /** The cart sections that need re-rendering once the lines land. */
  get #sectionIds() {
    const ids = new Set();

    for (const item of document.querySelectorAll('cart-items-component')) {
      if (item instanceof HTMLElement && item.dataset.sectionId) ids.add(item.dataset.sectionId);
    }

    return [...ids].join(',');
  }

  /** @param {SubmitEvent} event */
  #onSubmit = (event) => {
    const lines = this.#lines;

    // Nothing to post means something is wrong with the rendered card. Let the
    // native submit run rather than swallowing the shopper's click.
    if (lines.length === 0) return;

    event.preventDefault();
    // Keep the submit away from document-level cart handlers so the lines cannot
    // be added twice.
    event.stopPropagation();

    this.#add(lines);
  };

  /** @param {Array<{id: number, quantity: number}>} lines */
  #add(lines) {
    const button = this.#button;
    if (button?.disabled) return;

    this.#hideError();
    this.#setBusy(true);

    const totalQuantity = lines.reduce((total, line) => total + line.quantity, 0);
    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: lines.map((line) => ({ merchandiseId: String(line.id), quantity: line.quantity })),
        promise: deferred.promise,
      })
    );

    fetch(this.#route('cart_add_url', '/cart/add.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ items: lines, sections: this.#sectionIds }),
    })
      .then((response) => response.json())
      .then(async (response) => {
        // A cart error comes back with a `status` field, e.g. one of the bundled
        // products sold out since the page was rendered.
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent({
              error: response.message || 'Add to cart failed',
              code: 'INVALID',
              detail: { description: response.description, errors: response.errors },
            })
          );

          this.#showError(response.description || response.message || '');

          const cart = await this.#fetchCart();

          deferred.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
            detail: {
              didError: true,
              items: cart.items,
              source: 'pdp-bundle-form',
              itemCount: totalQuantity,
              productId: this.dataset.productId,
            },
          });

          return;
        }

        const cart = await this.#fetchCart();

        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            didError: false,
            items: cart.items,
            source: 'pdp-bundle-form',
            itemCount: totalQuantity,
            productId: this.dataset.productId,
            sections: response.sections,
          },
        });

        this.#showAdded();
      })
      .catch((error) => {
        console.error(error);
        deferred.reject(error);

        this.dispatchEvent(
          new CartErrorEvent({
            error: error?.message || 'Network error during add to cart',
            code: 'SERVICE_UNAVAILABLE',
          })
        );

        this.#showError('');
      })
      .finally(() => this.#setBusy(false));
  }

  async #fetchCart() {
    const response = await fetch(`${this.#route('cart_url', '/cart')}.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });

    if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);

    return response.json();
  }

  /**
   * @param {'cart_add_url' | 'cart_url'} name
   * @param {string} fallback
   */
  #route(name, fallback) {
    return (typeof Theme !== 'undefined' ? Theme.routes?.[name] : undefined) || fallback;
  }

  /** @param {boolean} busy */
  #setBusy(busy) {
    const button = this.#button;
    if (!button) return;

    button.disabled = busy;
    button.setAttribute('aria-busy', String(busy));
  }

  #showAdded() {
    const button = this.#button;
    const label = button?.querySelector('[data-pdp-bundle-label]');
    const addedText = button?.dataset.addedText;
    if (!button || !label || !addedText) return;

    clearTimeout(this.#labelTimeout);
    label.textContent = addedText;

    this.#labelTimeout = setTimeout(() => {
      label.textContent = button.dataset.defaultText ?? label.textContent;
    }, PdpBundleForm.ADDED_LABEL_DURATION);
  }

  /** @param {string} message */
  #showError(message) {
    const error = this.#error;
    if (!error) return;

    error.textContent = message || error.dataset.defaultText || '';
    error.hidden = !error.textContent;
  }

  #hideError() {
    const error = this.#error;
    if (!error) return;

    error.hidden = true;
  }
}

if (!customElements.get('pdp-bundle-form')) {
  customElements.define('pdp-bundle-form', PdpBundleForm);
}
